import type { AgentLinkPhase, DeviceSnapshot, HealthAction, HealthLevel, HealthSignal, HealthSubject } from '@krp/client-runtime'
import { linkPhase } from '@krp/client-runtime'

import type { AgentSessionModel } from '../../agent-session/model/agentSessionController'
import { OWN_AGENT_TEXTS, OWN_RECHECK_ACTION, OWN_RECHECK_LABEL, OWN_REGISTER_NO_AGENT, OWN_REGISTER_UNREACHABLE_ADVICE } from './healthTexts'

/**
 * Связь собственной кассы сигналами точки «Связь» (RP-UI-LINK, шаг 5): «Кассовый агент» и «Касса». Чистые функции от модели сессии
 * агента: ничего не читают и не отправляют. Слова проблем — агента дословно (ADR-8); свои тексты — только словарь `healthTexts.ts`.
 * Строки состояния связи живут только здесь и в словаре (страж `krp/connection-text`, шаг 7).
 */

export const OWN_AGENT_SUBJECT: HealthSubject = { kind: 'register', id: 'own.agent', title: 'Кассовый агент' }
export const OWN_REGISTER_SUBJECT: HealthSubject = { kind: 'register', id: 'own.register', title: 'Касса' }

const RECHECK: readonly HealthAction[] = [{ key: OWN_RECHECK_ACTION, label: OWN_RECHECK_LABEL }]
const NO_ACTIONS: readonly HealthAction[] = []

const AGENT_LEVELS: Readonly<Record<AgentLinkPhase, HealthLevel>> = {
  connected: 'ok',
  searching: 'unknown',
  lost: 'down',
  missing: 'down',
  choose: 'degraded',
  outdated: 'degraded',
  denied: 'down',
}

const iso = (ms: number | null): string | null => (ms === null ? null : new Date(ms).toISOString())

/** Связь собственной кассы двумя сигналами: кассовый агент на этом компьютере и сама касса. */
export interface OwnRegisterHealth {
  agent: HealthSignal
  register: HealthSignal
}

function agentSignal(session: AgentSessionModel, phase: AgentLinkPhase): HealthSignal {
  const agent = session.agent
  const words = OWN_AGENT_TEXTS[phase]
  const ok = phase === 'connected'
  return {
    subject: OWN_AGENT_SUBJECT,
    level: AGENT_LEVELS[phase],
    summary: ok && agent !== null ? `${words.summary} · порт ${agent.port}` : words.summary,
    problem: null,
    lastOkAt: iso(session.link.lastSeenAt),
    since: phase === 'lost' ? iso(session.link.lostSince) : null,
    advice: words.advice,
    actions: ok ? NO_ACTIONS : RECHECK,
    diagnostics: ok
      ? null
      : [
          `Кассовый агент: ${phase}`,
          ...(agent !== null ? [`Версия ${agent.hello.version}, порт ${agent.port}`] : []),
          `Найдено агентов: ${session.link.agents.length}; промахов подряд: ${session.link.misses}`,
          `Сайт: ${session.link.siteOrigin}`,
        ].join('\n'),
  }
}

function registerSignal(session: AgentSessionModel, phase: AgentLinkPhase): HealthSignal {
  const device: DeviceSnapshot | null = session.device
  const base = { subject: OWN_REGISTER_SUBJECT, lastOkAt: null, since: null, diagnostics: null }
  if (phase !== 'connected') {
    return { ...base, level: 'unknown', summary: OWN_REGISTER_NO_AGENT, problem: null, advice: null, actions: NO_ACTIONS }
  }
  if (session.deviceUnreachable) {
    // Слова агента, почему кассу прочитать нельзя, — в причине закрытой продажи (`gate.text`).
    const problem = session.gate.reason === 'device-unreachable' ? session.gate.text : null
    return { ...base, level: 'down', summary: 'Состояние не прочитано', problem, advice: OWN_REGISTER_UNREACHABLE_ADVICE, actions: RECHECK }
  }
  if (device === null) return { ...base, level: 'unknown', summary: 'Ещё не проверена', problem: null, advice: null, actions: RECHECK }
  const problems = device.problems.map((item) => item.text)
  const problem = problems.length === 0 ? null : problems.join(' ')
  const advice = device.problems.find((item) => item.action !== null)?.action ?? null
  switch (device.connection) {
    case 'ok':
      return device.canSell && device.problems.every((item) => item.severity !== 'block')
        ? { ...base, level: 'ok', summary: 'Отвечает, готова к продаже', problem: null, advice: null, actions: NO_ACTIONS }
        : { ...base, level: 'degraded', summary: 'Отвечает, но продавать нельзя', problem, advice, actions: RECHECK }
    case 'no-device':
      return { ...base, level: 'down', summary: 'Не отвечает', problem, advice: advice ?? OWN_REGISTER_UNREACHABLE_ADVICE, actions: RECHECK }
    case 'busy':
      return { ...base, level: 'degraded', summary: 'Занята другой программой', problem, advice, actions: RECHECK }
    case 'driver-missing':
      return { ...base, level: 'down', summary: 'Нет драйвера кассы', problem, advice, actions: RECHECK }
    case 'error':
      return { ...base, level: 'down', summary: 'Ошибка связи', problem, advice, actions: RECHECK }
  }
}

/**
 * Связь собственной кассы сигналами точки «Связь» (RP-UI-LINK, шаг 5): фаза связи с агентом (`linkPhase`) и состояние кассы
 * из его ответа. Пока агент не на связи, о кассе честно «не проверить» — прежний снимок устарел.
 */
export function ownRegisterHealth(session: AgentSessionModel, now: number = Date.now()): OwnRegisterHealth {
  const phase = linkPhase(session.link, now)
  return { agent: agentSignal(session, phase), register: registerSignal(session, phase) }
}
